import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text} from '@buncha/components/core/text';
import React, {useCallback} from 'react';
import {
  RecommendedItemCell,
  ReplacementItemDetails,
} from '@buncha/itemDetails/types/itemReplacement';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {CustomImage} from '@buncha/components/core/Image';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OOSCatalogItem} from '@buncha/itemDetails/types/itemDetails';
import {ShopperScreens} from '@buncha/home/navigation/types';
import SwapIcon from '@buncha/components/core/icon/SwapIcon';
import {formatCurrency} from '@buncha/utils/common';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';

interface ReplacementModalBodyProps {
  text: string;
  item?: ReplacementItemDetails | OOSCatalogItem;
  catalogItem?: RecommendedItemCell | OOSCatalogItem;
  rightText?: string;
  itemSubImage?: string;
  newItemSubImage?: string;
}

const ReplacementModalBody: React.FC<ReplacementModalBodyProps> = props => {
  const {text, item, catalogItem} = props;
  const muliFont = useFontTheme('muli');
  const theme = useColorThemeType();
  const {title} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const getImageComponent = useCallback((url: string, subUrl?: string) => {
    return (
      <Box style={styles.imageBox}>
        <CustomImage style={styles.image} source={url} />
        <If condition={subUrl}>
          <CustomImage style={styles.subImage} source={subUrl ?? ''} />
        </If>
      </Box>
    );
  }, []);

  const getItem = useCallback(
    (
      headerText: string,
      renderItem?:
        | ReplacementItemDetails
        | RecommendedItemCell
        | OOSCatalogItem,
      subImage?: string,
    ) => {
      return (
        <Box
          style={[
            styles.cellContainer,
            {
              borderColor: theme?.text?.gray?.[200],
            },
          ]}>
          <H6Text
            style={[
              styles.textStyle,
              styles.headerText,
              {
                fontFamily: muliFont[700].normal,
                color: theme?.text.blue[300],
              },
            ]}
            numberOfLines={1}>
            {headerText}
          </H6Text>
          {getImageComponent(renderItem?.itemImageUrl ?? '', subImage)}
          <H6Text
            style={[
              styles.textStyle,
              styles.priceText,
              {
                fontFamily: muliFont[700].normal,
                color: theme?.text?.blue?.[300],
              },
            ]}
            numberOfLines={1}>
            {formatCurrency(
              (renderItem?.price ?? 0) *
                (renderItem?.quantity ?? item?.quantity ?? 1),
            )}
            {'  '}
            <CustomText
              style={{
                fontFamily: muliFont[700].normal,
                color: theme?.text.gray[300],
              }}
              numberOfLines={1}>
              {`${formatCurrency(renderItem?.price ?? 0)} each`}
            </CustomText>
          </H6Text>
          <CustomText
            style={[
              styles.textStyle,
              {
                fontFamily: muliFont[700].normal,
                color: theme?.text.gray[100],
                marginTop: 2,
              },
            ]}
            numberOfLines={2}>
            {renderItem?.name}
          </CustomText>
        </Box>
      );
    },
    [
      getImageComponent,
      item?.quantity,
      muliFont,
      theme?.text.blue,
      theme?.text.gray,
    ],
  );

  return (
    <Box>
      <If condition={text}>
        <H6Text>{text}</H6Text>
      </If>
      <HBox style={styles.subContainer}>
        {getItem(title?.originalItem, item, props.itemSubImage)}
        <Box style={styles.iconContainer}>
          <SwapIcon />
        </Box>
        {getItem(
          props?.rightText ?? title?.substitutedItem,
          catalogItem,
          props.newItemSubImage,
        )}
      </HBox>
    </Box>
  );
};

export default React.memo(ReplacementModalBody);
